import { useQuery } from "react-query";
import { getAllSensors } from "@modules/Sensors/services/sensorCrud";
import { getTrees } from "@modules/Trees/services/treeCrud";
import { getAllTasks } from "@modules/Tasks/services/taskCrud";
import { getAllUsers } from "@modules/Users/services/userCrud";
import {
  getQueryConfig,
  REQUEST_PRIORITIES,
  createScheduledQueryFn,
} from "@helper/requestPriority";

/**
 * Hook for fetching paginated sensors with server-side pagination
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.pageSize - Number of items per page
 * @param params.search - Search query string
 * @param params.projectId - Optional project ID filter
 * @param params.ordering - Optional ordering field (e.g., "name", "-name")
 * @param params.enabled - Whether the query should run
 */
export const usePaginatedSensors = (params: {
  page: number;
  pageSize: number;
  search?: string;
  projectId?: string | number;
  ordering?: string;
  area?: string[];
  enabled?: boolean;
}) => {
  const {
    page,
    pageSize,
    search = "",
    projectId,
    ordering = "",
    area = [],
    enabled = true,
  } = params;

  const scheduledGetSensors = createScheduledQueryFn(
    () => {
      // Build params object based on projectId value
      const apiParams: any = {
        page,
        pageSize,
        search,
        ...(ordering && { ordering }),
        ...(area.length > 0 && { area: area.join(",") }),
      };

      // Add project filter parameter
      if (projectId === "without-project") {
        apiParams.without_project = true;
      } else if (projectId) {
        apiParams.project_id = projectId;
      }

      return getAllSensors(apiParams);
    },
    REQUEST_PRIORITIES.HIGH, // High priority for user-facing queries
    "getPaginatedSensors"
  );

  return useQuery(
    ["sensors-paginated", { page, pageSize, search, projectId, ordering, area }],
    scheduledGetSensors,
    {
      ...getQueryConfig(REQUEST_PRIORITIES.HIGH),
      enabled,
      keepPreviousData: true, // Keep previous page data while fetching new page (smooth transitions)
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: false, // Don't retry failed pagination requests
    }
  );
};

/**
 * Hook for fetching paginated trees with server-side pagination
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.pageSize - Number of items per page
 * @param params.search - Search query string
 * @param params.projectId - Optional project ID filter
 * @param params.ordering - Optional ordering field (e.g., "name", "-name")
 * @param params.enabled - Whether the query should run
 */
export const usePaginatedTrees = (params: {
  page: number;
  pageSize: number;
  search?: string;
  projectId?: string | number;
  ordering?: string;
  area?: string[];
  status?: string[];
  enabled?: boolean;
}) => {
  const {
    page,
    pageSize,
    search = "",
    projectId,
    ordering = "",
    area = [],
    status = [],
    enabled = true,
  } = params;

  const scheduledGetTrees = createScheduledQueryFn(
    () => {
      // Build params object based on projectId value
      const apiParams: any = {
        page,
        pageSize,
        search,
        ...(ordering && { ordering }),
        ...(area.length > 0 && { area: area.join(",") }),
        ...(status.length > 0 && { status: status.join(",") }),
      };

      // Add project filter parameter
      if (projectId === "without-project") {
        apiParams.without_project = true;
      } else if (projectId) {
        apiParams.project_id = projectId;
      }

      return getTrees(apiParams);
    },
    REQUEST_PRIORITIES.HIGH, // High priority for user-facing queries
    "getPaginatedTrees"
  );

  return useQuery(
    ["trees-paginated", { page, pageSize, search, projectId, ordering, area, status }],
    scheduledGetTrees,
    {
      ...getQueryConfig(REQUEST_PRIORITIES.HIGH),
      enabled,
      keepPreviousData: true, // Keep previous page data while fetching new page (smooth transitions)
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: false, // Don't retry failed pagination requests
    }
  );
};

/**
 * Hook for fetching paginated tasks with server-side pagination
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.pageSize - Number of items per page
 * @param params.search - Search query string
 * @param params.projectId - Optional project ID filter
 * @param params.ordering - Optional ordering field (e.g., "title", "-title")
 * @param params.enabled - Whether the query should run
 */
export const usePaginatedTasks = (params: {
  page: number;
  pageSize: number;
  search?: string;
  projectId?: string | number;
  ordering?: string;
  measureType?: string[];
  pruningType?: string[];
  duePeriod?: string[];
  status?: string[];
  priority?: string[];
  area?: string[];
  assignee?: string[];
  enabled?: boolean;
}) => {
  const {
    page,
    pageSize,
    search = "",
    projectId,
    ordering = "",
    measureType = [],
    pruningType = [],
    duePeriod = [],
    status = [],
    priority = [],
    area = [],
    assignee = [],
    enabled = true,
  } = params;

  const scheduledGetTasks = createScheduledQueryFn(
    () => {
      // Build params object based on projectId value
      const apiParams: any = {
        page,
        pageSize,
        search,
        ...(ordering && { ordering }),
        ...(measureType.length > 0 && { measure_type: measureType.join(",") }),
        ...(pruningType.length > 0 && { pruning_type: pruningType.join(",") }),
        ...(duePeriod.length > 0 && { due_period: duePeriod.join(",") }),
        ...(status.length > 0 && { status: status.join(",") }),
        ...(priority.length > 0 && { priority: priority.join(",") }),
        ...(area.length > 0 && { area: area.join(",") }),
        ...(assignee.length > 0 && { assignee: assignee.join(",") }),
      };

      // Add project filter parameter
      if (projectId === "without-project") {
        apiParams.without_project = true;
      } else if (projectId) {
        apiParams.project_id = projectId;
      }

      return getAllTasks(apiParams);
    },
    REQUEST_PRIORITIES.HIGH, // High priority for user-facing queries
    "getPaginatedTasks"
  );

  return useQuery(
    [
      "tasks-paginated",
      {
        page,
        pageSize,
        search,
        projectId,
        ordering,
        measureType,
        pruningType,
        duePeriod,
        status,
        priority,
        area,
        assignee,
      },
    ],
    scheduledGetTasks,
    {
      ...getQueryConfig(REQUEST_PRIORITIES.HIGH),
      enabled,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
};

/**
 * Hook for fetching paginated users with server-side pagination
 * @param params - Pagination parameters
 * @param params.page - Page number (1-indexed)
 * @param params.pageSize - Number of items per page
 * @param params.search - Search query string
 * @param params.ordering - Optional ordering field (e.g., "username", "-username")
 * @param params.enabled - Whether the query should run
 */
export const usePaginatedUsers = (params: {
  page: number;
  pageSize: number;
  search?: string;
  ordering?: string;
  enabled?: boolean;
}) => {
  const { page, pageSize, search = "", ordering = "", enabled = true } = params;

  const scheduledGetUsers = createScheduledQueryFn(
    () => {
      return getAllUsers({
        page,
        pageSize,
        search,
        ...(ordering && { ordering }),
      });
    },
    REQUEST_PRIORITIES.HIGH, // High priority for user-facing queries
    "getPaginatedUsers"
  );

  return useQuery(["users-paginated", { page, pageSize, search, ordering }], scheduledGetUsers, {
    ...getQueryConfig(REQUEST_PRIORITIES.HIGH),
    enabled,
    keepPreviousData: true, // Keep previous page data while fetching new page (smooth transitions)
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: false, // Don't retry failed pagination requests
  });
};
